import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button"; // Relative path
import { Input } from "../components/ui/input"; // Relative path
import { Label } from "../components/ui/label"; // Relative path
import { toast } from "sonner";
import logoSvg from "../assets/images/logo.svg"; // Relative path
import { useAuth } from "../context/AuthContext"; // Relative path
import { Loader2 } from "lucide-react"; // Import Loader2
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

const formSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters long"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ResetPasswordFormValues = z.infer<typeof formSchema>;

const ResetPasswordPage = () => {
  const { resetPassword, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [resetCode, setResetCode] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const codeFromQuery = searchParams.get("code");
    const emailFromQuery = searchParams.get("email");

    if (codeFromQuery && emailFromQuery) {
      setResetCode(codeFromQuery);
      setEmail(emailFromQuery);
    } else {
      toast.error(
        "Invalid or missing password reset details. Please request a new link."
      );
      navigate("/forgot-password");
    }
  }, [location.search, navigate]);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: ResetPasswordFormValues) => {
    if (!resetCode || !email) {
      toast.error("Reset information is missing. Please try the link again.");
      return;
    }
    try {
      await resetPassword(email, resetCode, values.newPassword);
      // AuthContext navigates to /login on success
    } catch (error) {
      // Error toast is handled by AuthContext
      console.error("Reset password error:", error);
    }
  };

  return (
    <div className="flex-grow bg-gray-50 py-12">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex items-center justify-center">
          <img
            src={logoSvg}
            alt="Commercial Property LK Logo"
            className="h-12 w-auto"
          />
        </Link>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Reset Your Password
        </h2>
        {!resetCode || !email ? (
          <p className="mt-2 text-center text-sm text-red-600">
            Invalid or expired password reset link. Please request a new one.
          </p>
        ) : (
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your new password for{" "}
            <span className="font-medium">{email}</span>.
          </p>
        )}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {resetCode && email ? (
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <Label
                  htmlFor="newPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  New Password
                </Label>
                <div className="mt-1">
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="••••••••"
                    {...form.register("newPassword")}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-propertylk-blue focus:border-propertylk-blue sm:text-sm"
                    disabled={isLoading}
                  />
                  {form.formState.errors.newPassword && (
                    <p className="mt-1 text-xs text-red-600">
                      {form.formState.errors.newPassword.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  Confirm New Password
                </Label>
                <div className="mt-1">
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    {...form.register("confirmPassword")}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-propertylk-blue focus:border-propertylk-blue sm:text-sm"
                    disabled={isLoading}
                  />
                  {form.formState.errors.confirmPassword && (
                    <p className="mt-1 text-xs text-red-600">
                      {form.formState.errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Button
                  type="submit"
                  className="w-full flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-propertylk-blue hover:bg-propertylk-darkblue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-propertylk-blue disabled:opacity-75"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting Password...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <div className="text-center">
              <p className="text-sm text-gray-700 mb-4">
                This password reset link is invalid or has expired.
              </p>
              <Button
                onClick={() => navigate("/forgot-password")}
                className="w-full bg-propertylk-blue hover:bg-propertylk-darkblue"
              >
                Request a New Reset Link
              </Button>
            </div>
          )}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm font-medium text-propertylk-blue hover:text-propertylk-darkblue"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
